import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { useLocationUrl } from "@redotech/react-util/location";
import {
  getTeamInfoByStoreUrl,
  getTeamInfoByWidgetId,
} from "@redotech/redo-api-client/team";
import { isWithinHours } from "@redotech/redo-model/is-within-hours";
import { TeamInfo } from "@redotech/redo-model/team";
import { RedoClientContext } from "@redotech/redo-web/client";
import { useWidgetButtonStyle } from "@redotech/redo-web/customer-widget/widget-button";
import { CUSTOMER_WIDGET_ELEMENT_ID } from "@redotech/redo-web/utils/shared-conf";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront";
import { isDef } from "@redotech/util/checks";
import { ReactNode, createContext, memo, useMemo } from "react";

// In Vite/ESM environments, document.currentScript may be null
const scriptUrl = document.currentScript
  ? new URL((document.currentScript as HTMLScriptElement).src)
  : new URL(window.location.href);

export interface SetTeamInfo {
  (teamInfo: TeamInfo | undefined): void;
}

export const TeamInfoContext = createContext<TeamInfo | undefined>(undefined);

export const StorefrontClientContext = createContext<
  ShopifyStorefrontClient | undefined
>(undefined);

export const TeamInfoProvider = memo(function TeamInfoProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const url = useLocationUrl();
  const widgetId =
    url.searchParams.get("widgetId") || scriptUrl.searchParams.get("widgetId");
  const storeUrl = scriptUrl.searchParams.get("storeUrl");
  if (!widgetId && !storeUrl) {
    throw new Error("Widget ID or storeUrl required");
  }
  const client = useRequiredContext(RedoClientContext);

  const teamInfoLoad = useLoad(
    async (signal) => {
      let teamInfoResponse: TeamInfo | undefined;
      if (widgetId) {
        teamInfoResponse = await getTeamInfoByWidgetId(client, {
          widgetId,
          signal,
        });
      } else if (storeUrl) {
        teamInfoResponse = await getTeamInfoByStoreUrl(client, {
          storeUrl,
          signal,
        });
      } else {
        return undefined;
      }

      if (isDef(teamInfoResponse)) {
        const withinHours = isWithinHours({
          availabilityEnabled: !!teamInfoResponse.widgetHoursEnabled,
          timezone: teamInfoResponse.widgetHoursTimezone || "",
          availableHours: teamInfoResponse.widgetHours,
        });
        // Hide the support features if the team doesn't have them or it's after
        // hours and they have selected to hide the widget after hours.
        const supportFeaturesEnabled =
          teamInfoResponse.supportFeaturesEnabled &&
          !(teamInfoResponse.hideWidgetAfterHours && !withinHours);
        return { ...teamInfoResponse, supportFeaturesEnabled };
      } else {
        return undefined;
      }
    },
    [client, widgetId, storeUrl],
  );

  const storefrontClient = useMemo(() => {
    if (
      !teamInfoLoad.value?.storeUrl ||
      !teamInfoLoad.value?.storefrontAccessToken
    ) {
      return;
    }

    const storefrontClient = new ShopifyStorefrontClient(
      teamInfoLoad.value.storeUrl,
      teamInfoLoad.value.storefrontAccessToken,
    );
    return storefrontClient;
  }, [teamInfoLoad.value]);

  const customerWidgetElement = document.getElementById(
    CUSTOMER_WIDGET_ELEMENT_ID,
  );

  useWidgetButtonStyle(
    customerWidgetElement || document.documentElement,
    teamInfoLoad.value?.widgetPrimaryColor,
    teamInfoLoad.value?.widgetSecondaryColor,
    teamInfoLoad.value?.widgetSecondaryColorEnabled,
  );

  return (
    <TeamInfoContext.Provider value={teamInfoLoad.value}>
      <StorefrontClientContext.Provider value={storefrontClient}>
        {children}
      </StorefrontClientContext.Provider>
    </TeamInfoContext.Provider>
  );
});
